import { Badge } from '@design-system/components/badge/badge';
import { clsx } from 'clsx';
import { SearchInput } from '@design-system/components/searchinput';
import { Typography } from '@design-system/components/typography/typography';
import { PrimaryButton } from '@client/new/components/button';
import { Link } from 'react-router';
import { PATHS } from '@client/routes';
import { Skeleton } from '@mui/material';
import * as styles from './InfluencerList.view.css';
import { CreatorsListStats } from '@client/pages/admin/CreatorsListStats';
import { Tier } from '@client/new/components/Tier/Tier';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowDown,
  faArrowUp,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  FALLBACK_AVATAR_URL,
  InfluencerSortField,
} from '../view-model/InfluencerList.viewModel.mappers';
import auth, { isProduction } from '@client/components/auth';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { FilterBar } from './components/FilterBar';
import {
  InfluencerListVM,
  InfluencerRowVM,
} from '../view-model/InfluencerList.viewModel';

const HeaderCell = (p: {
  label: string;
  sortField?: InfluencerSortField;
  vm: InfluencerListVM;
}) => {
  const isSorted = p.sortField && p.vm.sorting.field === p.sortField;
  const arrowIcon = p.vm.sorting.direction === 'asc' ? faArrowUp : faArrowDown;

  const content = (
    <>
      <Typography variant="span" size="medium" fontWeight="medium">
        {p.label}
      </Typography>
      {isSorted ? <FontAwesomeIcon icon={arrowIcon} size="xs" /> : null}
    </>
  );

  if (!p.sortField) {
    return <div className={styles.headerCell}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={`${styles.headerCell} ${styles.sortableHeader}`}
      aria-sort={
        isSorted
          ? p.vm.sorting.direction === 'asc'
            ? 'ascending'
            : 'descending'
          : undefined
      }
      onClick={() => {
        if (p.sortField) p.vm.dispatch('ToggleSort', p.sortField);
      }}
    >
      {content}
    </button>
  );
};

const toCustomerioProfileUrl = (influencerId: number) => {
  const workspaceId = isProduction() ? '204139' : '204160';
  return `https://fly.customer.io/workspaces/${workspaceId}/journeys/people/${influencerId}`;
};

const ROW_HEIGHT = 60;
const OVERSCAN = 10;

const useWindowVirtualization = (itemCount: number) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });

  useEffect(() => {
    const update = () => {
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const viewportTop = -rect.top;
      const viewportBottom = viewportTop + window.innerHeight;

      const start = Math.max(
        0,
        Math.floor(viewportTop / ROW_HEIGHT) - OVERSCAN,
      );
      const end = Math.min(
        itemCount - 1,
        Math.ceil(viewportBottom / ROW_HEIGHT) + OVERSCAN,
      );

      setVisibleRange({ start, end });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [itemCount]);

  return { containerRef, visibleRange, totalHeight: itemCount * ROW_HEIGHT };
};

const InfluencerRow = memo(
  (p: {
    row: InfluencerRowVM;
    isAdministrator: boolean;
    updateTier: InfluencerListVM['updateTier'];
    dispatch: InfluencerListVM['dispatch'];
  }) => {
    const { row } = p;

    return (
      <div className={styles.row}>
        <Link
          className={styles.creatorLink}
          to={PATHS.HOME_CREATOR(row.id)}
          onClick={() => {
            trackSessionEvent('ImpersonateCreator', {
              influencerId: row.id,
              name: `${row.fullName} /${row.customURL}`,
            });
          }}
        >
          <img
            className={styles.avatar}
            src={row.avatarUrl}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = FALLBACK_AVATAR_URL;
            }}
          />
          <div className={styles.creatorMeta}>
            <Typography
              variant="span"
              size="medium"
              fontWeight="bold"
              className={styles.creatorName}
            >
              {row.truncatedName} ({row.id})
            </Typography>
            <Typography
              variant="span"
              size="medium"
              color="N-60"
              className={styles.creatorSubline}
            >
              /{row.customURL}
            </Typography>
          </div>
        </Link>

        <div
          className={clsx(
            styles.badgeCell,
            row.businessModelLabel === 'Legacy' && styles.badgeCellGray,
          )}
        >
          <Badge label={row.businessModelLabel} />
        </div>

        <div className={styles.socialsCell}>
          <CreatorsListStats
            profileInstagram={row.profileInstagram || undefined}
            instagramFollowersCount={row.instagramFollowersCount || undefined}
            profileTiktok={row.profileTiktok || undefined}
            profileYoutubeChannel={row.profileYoutubeChannel || undefined}
          />
        </div>

        <Tier
          value={row.tier ?? undefined}
          onChange={(tier) =>
            p.updateTier({
              influencerId: row.id,
              tier,
            })
          }
          loading={row.isTierUpdating}
          compact
        />

        <Typography variant="span" size="medium" className={styles.dateCell}>
          {row.publishedAtLabel}
        </Typography>
        <Typography variant="span" size="medium" className={styles.dateCell}>
          {row.createdAtLabel}
        </Typography>

        <div className={styles.actionCell}>
          <Tooltip variant="dark" title="Landing page">
            <a
              href={`${import.meta.env.VITE_LANDING_PAGE_URL}/${row.customURL}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.actionLink}
              onClick={(e) => e.stopPropagation()}
              aria-label="Open landing page"
            >
              <FontAwesomeIcon icon={faExternalLink} />
            </a>
          </Tooltip>
          <Tooltip variant="dark" title="Customer.io">
            <a
              href={toCustomerioProfileUrl(row.id)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.actionLink}
              onClick={(e) => e.stopPropagation()}
              aria-label="Open Customer.io profile"
            >
              CIO
            </a>
          </Tooltip>
          {p.isAdministrator ? (
            <button
              className={styles.deleteButton}
              disabled={row.isDeleting}
              onClick={() => {
                const shouldDelete = window.confirm(
                  'Are you sure you want to delete this creator?',
                );
                if (!shouldDelete) return;
                p.dispatch('DeleteInfluencer', row.id);
              }}
              type="button"
              aria-label="Delete creator"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          ) : null}
        </div>
      </div>
    );
  },
);

const useStickyTopHeight = () => {
  const stickyTopRef = useRef<HTMLDivElement>(null);
  const [stickyTopHeight, setStickyTopHeight] = useState(0);

  useEffect(() => {
    const el = stickyTopRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setStickyTopHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { stickyTopRef, stickyTopHeight };
};

export const InfluencerListView = (p: InfluencerListVM) => {
  const tableState = p.tableState;
  const isAdministrator = auth.isAdministrator();
  const filteredSorted =
    tableState.type === 'fetched' ? tableState.value.filteredSorted : [];
  const { containerRef, visibleRange, totalHeight } = useWindowVirtualization(
    filteredSorted.length,
  );
  const { stickyTopRef, stickyTopHeight } = useStickyTopHeight();

  const visibleRows = useMemo(
    () =>
      filteredSorted
        .slice(visibleRange.start, visibleRange.end + 1)
        .map(p.toRowVM),
    [filteredSorted, visibleRange, p.toRowVM],
  );

  const summary =
    tableState.type === 'fetched'
      ? `Showing ${tableState.value.visibleCount} of ${tableState.value.totalCount}`
      : tableState.type === 'fetch-error'
      ? 'Could not load creators'
      : 'Loading creators...';

  return (
    <div className={styles.page}>
      <div className={styles.stickyTop} ref={stickyTopRef}>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <Typography variant="headline" size="large">
              Creators
            </Typography>
            <Typography variant="span" size="small" color="N-60">
              {summary}
            </Typography>
          </div>
          <Link to={PATHS.ADMIN_NEW_CREATOR}>
            <PrimaryButton>Add New Creator</PrimaryButton>
          </Link>
        </div>

        <div className={styles.filtersRow}>
          <SearchInput
            placeholder="Search by name, URL or creator id"
            value={p.search}
            onChange={(event) => p.dispatch('SetSearch', event.target.value)}
            onClear={() => p.dispatch('SetSearch', '')}
          />
          <FilterBar
            filterRules={p.filterRules}
            filterFieldConfigs={p.filterFieldConfigs}
            filterPresets={p.filterPresets}
            dispatch={p.dispatch}
          />
        </div>
      </div>

      <div
        className={styles.table}
        style={
          {
            '--sticky-top-height': `${stickyTopHeight}px`,
          } as React.CSSProperties
        }
      >
        <div className={styles.tableScrollable}>
          <div className={styles.tableHeader}>
            <HeaderCell label="Creator" sortField="name" vm={p} />
            <HeaderCell label="Business model" vm={p} />
            <HeaderCell label="Socials" vm={p} />
            <HeaderCell label="Tier" vm={p} />
            <HeaderCell label="Go-live date" sortField="publishedAt" vm={p} />
            <HeaderCell label="Created at" sortField="createdAt" vm={p} />
            <HeaderCell label="" vm={p} />
          </div>
          <div className={styles.tableBody} ref={containerRef}>
            {tableState.type === 'fetching' ||
            tableState.type === 'not-fetched' ? (
              <SkeletonTableBody />
            ) : tableState.type === 'fetch-error' ? (
              <div className={styles.error}>{tableState.value}</div>
            ) : filteredSorted.length ? (
              <div style={{ height: totalHeight, position: 'relative' }}>
                {visibleRows.map((row, i) => (
                  <div
                    key={row.id}
                    style={{
                      position: 'absolute',
                      top: (visibleRange.start + i) * ROW_HEIGHT,
                      height: ROW_HEIGHT,
                      width: '100%',
                    }}
                  >
                    <InfluencerRow
                      row={row}
                      isAdministrator={isAdministrator}
                      updateTier={p.updateTier}
                      dispatch={p.dispatch}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                No creators match selected filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SKELETON_ROW_COUNT = 8;

const SkeletonRow = () => (
  <div className={styles.skeletonRow}>
    <div className={styles.creatorLink}>
      <Skeleton variant="circular" width={32} height={32} />
      <div className={styles.creatorMeta}>
        <Skeleton variant="text" width={140} className={styles.skeletonCell} />
        <Skeleton variant="text" width={80} className={styles.skeletonCell} />
      </div>
    </div>
    <Skeleton variant="text" width={60} className={styles.skeletonCell} />
    <Skeleton variant="text" width={80} className={styles.skeletonCell} />
    <Skeleton
      variant="rounded"
      width={48}
      height={24}
      className={styles.skeletonCell}
    />
    <Skeleton variant="text" width={70} className={styles.skeletonCell} />
    <Skeleton variant="text" width={70} className={styles.skeletonCell} />
    <Skeleton variant="text" width={70} className={styles.skeletonCell} />
    <div />
  </div>
);
const SkeletonTableBody = () => (
  <>
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
      <SkeletonRow key={i} />
    ))}
  </>
);
